/**
 * A super admin unlocks a verification-locked term row by marking the row itself, so the mark goes
 * out with every save of the schedule. The server applies an unlocked row exactly as it is shown and
 * rejects changes to any locked row without the mark; it never stores the mark.
 */
const UNLOCK_MARK = "superAdminEdit";

const SCHEDULE_FIELDS = [
  "date",
  "censusDate",
  "units",
  "termFee",
  "termFeeManual",
  "materialFee",
  "discount",
  "discountManual",
];

export const isTermRowUnlocked = (row) => row?.[UNLOCK_MARK] === true;

export const unlockTermRow = (rows, rowId) =>
  rows.map((row) => (row.id === rowId ? { ...row, [UNLOCK_MARK]: true } : row));

/** What an unlocked row is put back to when the super admin cancels the edit. */
export const scheduleFieldsOf = (row) =>
  Object.fromEntries(SCHEDULE_FIELDS.map((field) => [field, row[field]]));

const withoutUnlockMark = (row) => {
  const { [UNLOCK_MARK]: _unlocked, ...locked } = row;
  return locked;
};

/**
 * A save applies every unlocked row, so it ends the unlock: the rows lock again as they were saved,
 * and there is nothing left for a cancel to put back.
 */
export const relockSavedSchedule = (info) => {
  const rows = info?.termSchedule;
  if (!Array.isArray(rows) || !rows.some(isTermRowUnlocked)) return info;
  return {
    ...info,
    termSchedule: rows.map((row) => (isTermRowUnlocked(row) ? withoutUnlockMark(row) : row)),
  };
};

/**
 * Locks the row again. Its schedule goes back to how it was when it was unlocked, since a locked row
 * the server finds changed is refused, while payments recorded in the meantime stay.
 */
export const cancelTermRowUnlock = (rows, rowId, original) =>
  rows.map((row) => {
    if (row.id !== rowId) return row;
    const locked = withoutUnlockMark(row);
    return original ? { ...locked, ...original } : locked;
  });
